import React from 'react';
import { ICampaignDeliverables } from '../../../interfaces/campaign.interface';
import {
    AssetType,
    DeliverableTask,
    Platform,
    SUB_DELIVERABLE_PLATFORM_ARRAY,
    getAssetTypeForPlatform,
} from '../../../constants/campaign';
import { Dropdown, Form } from 'react-bootstrap';

interface IProps {
    index: number;
    deliverable: ICampaignDeliverables;
    deleteSubDeliverable: (deliverableNumber: number, subDeliverableNumber: number) => void;
    updateDeliverable: (index: number, deliverable: ICampaignDeliverables) => void;
}

const SubDeliverable = ({
    index,
    deliverable,
    deleteSubDeliverable,
    updateDeliverable,
}: IProps) => {
    const updateDeliverableTask = (task: DeliverableTask) => {
        if (deliverable.task === task) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.task = task;
        updatedDeliverable.content = '';
        if (task === DeliverableTask.RESIZE) {
            updatedDeliverable.platform = Platform.INSTAGRAM;
            updatedDeliverable.asset_type = getAssetTypeForPlatform(Platform.INSTAGRAM)[0];
        } else if (task === DeliverableTask.VARIANT) {
            updatedDeliverable.platform = undefined;
            updatedDeliverable.asset_type = undefined;
            updatedDeliverable.variant_count = 2;
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverablePlatform = (platform: Platform) => {
        if (deliverable.platform === platform) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.platform = platform;
        if (platform === Platform.CUSTOM) {
            updatedDeliverable.width = 100;
            updatedDeliverable.height = 100;
        }
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableAssetType = (assetType: AssetType) => {
        if (deliverable.asset_type === assetType) return;
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.asset_type = assetType;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableContent = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.content = event.target.value;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableWidthAndHeight = (width: number, height: number) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.width = width;
        updatedDeliverable.height = height;
        updateDeliverable(index, updatedDeliverable);
    };

    const updateDeliverableVariantCount = (count: number) => {
        const updatedDeliverable: ICampaignDeliverables = { ...deliverable };
        updatedDeliverable.variant_count = count;
        updateDeliverable(index, updatedDeliverable);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // setIsEditing(false);
        }
    };

    return (
        <div className="tw-w-full tw-flex tw-items-start tw-gap-x-2">
            <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-font-bold tw-text-base tw-p-3"></span>
            <div className="tw-ml-[60px] md:tw-ml-[128px] tw-grow tw-flex tw-items-center tw-justify-between tw-gap-x-2 tw-py-2 tw-px-4 tw-border tw-rounded-md tw-border-gray-250">
                <div className="tw-grow tw-flex tw-items-center tw-gap-x-2">
                    <Dropdown
                        onSelect={(task) => updateDeliverableTask(task as DeliverableTask)}
                        className="custom-dropdown_alt"
                    >
                        <Dropdown.Toggle
                            variant="secondary"
                            id="dropdown-basic"
                            className="dropdown__toggle !tw-font-bold"
                        >
                            {deliverable.task}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="dropdown__toggle--menu">
                            <Dropdown.Item eventKey={DeliverableTask.RESIZE}>
                                {DeliverableTask.RESIZE}
                            </Dropdown.Item>
                            <Dropdown.Item eventKey={DeliverableTask.VARIANT}>
                                {DeliverableTask.VARIANT}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>

                    {deliverable.task === DeliverableTask.RESIZE && (
                        <>
                            <p>to</p>
                            <Dropdown
                                onSelect={(platform) =>
                                    updateDeliverablePlatform(platform as Platform)
                                }
                                className="custom-dropdown_alt"
                            >
                                <Dropdown.Toggle
                                    variant="secondary"
                                    id="dropdown-platform"
                                    className="dropdown__toggle !tw-font-bold"
                                >
                                    {deliverable.platform || 'Select Platform'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown__toggle--menu">
                                    {SUB_DELIVERABLE_PLATFORM_ARRAY.map((platform, index) => (
                                        <div key={index}>
                                            {' '}
                                            <Dropdown.Item eventKey={platform} key={platform}>
                                                {platform}
                                            </Dropdown.Item>
                                        </div>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            {deliverable.platform === Platform.CUSTOM ? (
                                <>
                                    <div className="tw-flex tw-items-center gap-1">
                                        <Form.Control
                                            type="number"
                                            placeholder="Width"
                                            value={deliverable.width}
                                            onChange={(e) =>
                                                updateDeliverableWidthAndHeight(
                                                    Number(e.target.value),
                                                    deliverable.height ? deliverable.height : 100,
                                                )
                                            }
                                            className="tw-w-[100px]"
                                        />
                                        <p>px</p>
                                    </div>
                                    <p>X</p>
                                    <div className="tw-flex tw-items-center gap-1">
                                        <Form.Control
                                            type="number"
                                            placeholder="Height"
                                            value={deliverable.height}
                                            onChange={(e) =>
                                                updateDeliverableWidthAndHeight(
                                                    deliverable.width ? deliverable.width : 100,
                                                    Number(e.target.value),
                                                )
                                            }
                                            className="tw-w-[100px]"
                                        />
                                        <p>px</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Dropdown
                                        onSelect={(assetType) =>
                                            updateDeliverableAssetType(assetType as AssetType)
                                        }
                                        className="custom-dropdown_alt "
                                    >
                                        <Dropdown.Toggle
                                            variant="secondary"
                                            id="dropdown-asset-type"
                                            className="dropdown__toggle !tw-font-bold"
                                        >
                                            {deliverable.asset_type || 'Select Asset Type'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="dropdown__toggle--menu">
                                            {getAssetTypeForPlatform(deliverable.platform).map(
                                                (assetType, index) => (
                                                    <Dropdown.Item eventKey={assetType} key={index}>
                                                        {assetType}
                                                    </Dropdown.Item>
                                                ),
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </>
                            )}
                        </>
                    )}
                    {deliverable.task === DeliverableTask.VARIANT && (
                        <>
                            <Form.Control
                                type="number"
                                min={1}
                                max={4}
                                value={deliverable.variant_count}
                                onChange={(e) =>
                                    updateDeliverableVariantCount(Number(e.target.value))
                                }
                                className="tw-w-[100px]"
                            />
                            <Form.Control
                                as="textarea"
                                placeholder="Describe what to build"
                                className="!tw-py-1.5"
                                value={deliverable.content}
                                onChange={updateDeliverableContent}
                                rows={1}
                                onKeyDown={handleKeyDown}
                            />
                        </>
                    )}
                </div>

                <div
                    className="tw-flex tw-items-center tw-justify-center tw-border tw-rounded-md tw-p-2 tw-border-gray-250 tw-cursor-pointer"
                    onClick={() =>
                        deleteSubDeliverable(
                            deliverable.deliverable_number,
                            deliverable.sub_deliverable ? deliverable.sub_deliverable : 1,
                        )
                    }
                >
                    <i className="ph-bold ph-trash lg"></i>
                </div>
            </div>
            <div className="tw-flex tw-items-center tw-jutify-center tw-w-[42px] tw-h-[42px]"></div>
        </div>
    );
};

export default SubDeliverable;
